import { Component, EventEmitter, Input, OnInit, Output, Inject, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import {
    AccountTicketsNgrxFacade,
    AccountTicketsService,
    MediumFacade,
    MediumGroup,
    MediumGroupType,
    NotificationFacade,
    RelatedCardFacade,
    SessionService
} from '@ridango/components';
import { defaultIfEmpty, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators';
import {
    BaseComponent,
    Ticket,
    AppStartupService,
    TicketActionHttpErrorResponse,
    TicketActionErrorResponse
} from '@ridango/core';
import { DeviceIdService } from '@ridango/core/lib/services';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import Env from '@ridango/core/env';
import { HttpErrorResponse } from '@angular/common/http';
import {
    FormatOption,
    SelectPeriodLendModalComponent
} from './select-period-lend-modal/select-period-lend-modal.component';
import { Temporal } from '@js-temporal/polyfill';

@Component({
    selector: 'rwidget-ticket-action-modal',
    templateUrl: './ticket-action-modal.component.html',
    styleUrls: ['./ticket-action-modal.component.scss']
})
export class TicketActionModalComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() locale: string;
    @Input() docNum: string;
    @Input() isRecall: boolean;
    @Input() action: string;
    @Input() form: FormGroup;
    @Input() ticket: Ticket;
    @Output() refreshTickets: EventEmitter<void> = new EventEmitter();

    readonly ACCOUNT_REQUIRED = 'ACCOUNT_REQUIRED';
    readonly CONFIRM_EMAIL_SENDING = 'CONFIRM_EMAIL_SENDING';

    validUntilMinDate = moment().add(4, 'h').format();
    // Not really sure why we need this, but mixing date and datetime formats caused issues with test
    validUntil = { datetime: moment().add(4, 'h').format('yyyy-MM-DDTHH:mm:ss') };
    validUntilFormatted: string = moment.tz(this.validUntil.datetime, this.env.TIMEZONE).format('DD.MM.yyyy HH:mm');
    selectedLendTime: FormatOption;
    allCards$ = this.relatedCardFacade.typeTravelcardCards$;
    mediumGroups$: Observable<MediumGroup[]> = this.mediumFacade.availableMediumGroups$;

    travelCards: any[];
    shouldShowCloseButton: boolean;
    isLoading = false;

    constructor(
        private modalCtrl: ModalController,
        private iconRegistry: MatIconRegistry,
        private sanitizer: DomSanitizer,
        private session: SessionService,
        private mediumFacade: MediumFacade,
        private relatedCardFacade: RelatedCardFacade,
        private accountTicketsService: AccountTicketsService,
        private notification: NotificationFacade,
        private accountTicketsFacade: AccountTicketsNgrxFacade,
        private router: Router,
        private translate: TranslateService,
        private deviceIdService: DeviceIdService,
        private appStartUp: AppStartupService,
        @Inject('ENV') public env: Env
    ) {
        super();
        this.addIcon('actionAdd', 'assets/img/add.svg');
        this.addIcon('actionInfo', 'assets/img/alert-info.svg');
    }

    ngOnInit(): void {
        this.shouldShowCloseButton =
            this.action !== this.ACCOUNT_REQUIRED && this.action !== this.CONFIRM_EMAIL_SENDING;
        this.initTravelCards();

        if (this.env.SHOW_SELECTOR_TIME_LEND_TICKET && !this.selectedLendTime) {
            this.setDefaultSelectedLendTime();
        }
    }

    get isDisableSendTicketButton(): boolean {
        return this.form.controls.identifier.errors?.required || this.form.controls.identifier.errors?.identifier;
    }

    initTravelCards() {
        if (this.action === 'RETURN_TICKET') {
            if (this.session.getLoginStatus()) {
                this.relatedCardFacade.fetchAllRelatedCards();
            }

            this.mediumFacade.resetSelectedMedium();

            this.allCards$
                .pipe(
                    takeUntil(this.componentDestroyed$),
                    mergeMap((cards) => {
                        return this.mediumGroups$.pipe(
                            takeUntil(this.componentDestroyed$),
                            switchMap((groups) => {
                                const group = groups.find(({ type }) => type === MediumGroupType.Travelcard);
                                if (group) {
                                    return of(
                                        cards.filter(({ card_type }) => group.travelcardTypes.includes(card_type))
                                    );
                                } else {
                                    return of(cards);
                                }
                            })
                        );
                    })
                )
                .subscribe((cards) => (this.travelCards = cards));
        }
    }

    async cancel(): Promise<void> {
        await this.modalCtrl.dismiss(null, 'cancel');
    }

    addIcon(alias: string, path: string): void {
        this.iconRegistry.addSvgIcon(alias, this.sanitizer.bypassSecurityTrustResourceUrl(path));
    }

    addTravelCard() {
        this.modalCtrl.dismiss();
        this.router.navigate([this.translate.currentLang, 'account', 'cards', 'add']);
    }

    async cancelSendTicket() {
        await this.modalCtrl.dismiss(null, 'cancel');
    }

    async sendTicket() {
        await this.modalCtrl.dismiss(true, 'cancel');
    }

    private shouldUseDeviceToken(deviceCustomerId: number | string | null): boolean {
        return deviceCustomerId != null && deviceCustomerId === (this.ticket?.customerAccountId ?? null);
    }

    private transferTicket(identifier: string): void {
        this.deviceIdService
            .getDeviceCustomerId()
            .pipe(
                take(1),
                defaultIfEmpty(null),
                switchMap((deviceCustomerId) => {
                    const useDeviceToken = this.shouldUseDeviceToken(deviceCustomerId);
                    return this.accountTicketsService.transferTicket(identifier, this.docNum, useDeviceToken);
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(
                () => this.onTicketTransferSuccess(),
                (e: TicketActionHttpErrorResponse) => this.handleTicketActionError(e.error)
            );
    }

    private async onTicketTransferSuccess(): Promise<void> {
        this.isLoading = false;
        this.notification.removeAll();
        this.accountTicketsFacade.resetAccountTickets();
        this.refreshTickets.emit();
        await this.modalCtrl.dismiss().catch(() => undefined);
        await this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
        this.notification.success(this.translate.instant('TICKET_TRANSFER_SUCCESSFUL'), { showAfterRouteChange: true });
    }

    private checkEmailReceiverIsCustomer(email: string): void {
        if (this.env.IS_NEW_AFC) {
            this.confirmationSend(email);
        } else {
            this.accountTicketsService
                .isTransferTicketEmailReceiverCustomer({ email })
                .pipe(take(1))
                .subscribe(
                    (response) => this.onCheckEmailReceiverSuccess(response, email),
                    (error: HttpErrorResponse) => this.onCheckEmailReceiverFailure(error, email)
                );
        }
    }

    private onCheckEmailReceiverSuccess(response, email: string): void {
        if (response && response.status === 200) {
            this.confirmationSend(email);
        } else {
            console.warn('Unexpected response', response);
        }
    }

    private confirmationSend(email: string) {
        this.isLoading = false;
        const cssClass = 'ticket-confirm-to-send-ticket-modal';
        this.createSendModal(cssClass, this.CONFIRM_EMAIL_SENDING, email);
    }

    private async onCheckEmailReceiverFailure(error: HttpErrorResponse, email: string): Promise<void> {
        this.isLoading = false;
        if (error.status === 409) {
            const cssClass = 'ticket-confirm-to-proceed-modal';
            this.createSendModal(cssClass, this.ACCOUNT_REQUIRED, email);
        } else {
            this.notification.error(this.translate.instant('TICKET_TRANSFER_FAILED'));
        }
    }

    async openPeriodModal() {
        const modal = await this.modalCtrl.create({
            component: SelectPeriodLendModalComponent,
            cssClass: 'select-period-modal',
            componentProps: {
                selected: this.selectedLendTime,
                expirationDate: this.ticket.ending
            }
        });
        modal.present();

        const { data } = await modal.onWillDismiss();

        if (!data) return;

        this.selectedLendTime = data;
        this.validUntil.datetime = data.date;
    }

    async createSendModal(cssClass: string, action: string, email: string) {
        await this.modalCtrl.dismiss();

        const modal = await this.modalCtrl.create({
            component: TicketActionModalComponent,
            cssClass: cssClass,
            componentProps: {
                action,
                refreshTickets: this.refreshTickets,
                locale: this.translate.currentLang,
                form: this.form,
                docNum: this.docNum,
                ticket: this.ticket
            }
        });

        modal.onDidDismiss().then((data) => {
            if (data.data === true) {
                this.transferTicket(email);
            }
        });

        await modal.present();
    }

    get disableLendButton() {
        if (this.isLoading) return true;
        if (!this.env.SHOW_SELECTOR_TIME_LEND_TICKET) return false;

        return !this.selectedLendTime || this.form.controls.email.errors;
    }

    async submit() {
        if (!this.form.valid) {
            this.handleInvalidForm();
            return;
        }

        switch (this.action) {
            case 'SEND_TICKET':
                this.handleSendTicket();
                break;
            case 'RETURN_TICKET':
                this.handleReturnTicket();
                break;
            case 'LEND_TICKET':
                this.handleLendTicket();
                break;
            case 'RETURN_LEND_TICKET':
            case 'RECALL_LEND_TICKET':
                this.handleReturnOrRecallLendTicket();
                break;
        }
    }

    private handleInvalidForm(): void {
        this.form.markAllAsTouched();
    }

    private handleSendTicket(): void {
        const identifier = this.form.get('identifier').value;
        this.isLoading = true;
        if (this.env?.CHECK_TICKET_EMAIL_RECEIVER_IS_CUSTOMER) {
            this.checkEmailReceiverIsCustomer(identifier);
        } else {
            this.transferTicket(identifier);
        }
    }

    private handleReturnTicket(): void {
        this.accountTicketsService
            .refundTicket(this.form.get('travelCardNumber').value, this.docNum)
            .pipe(take(1))
            .subscribe(
                async () => {
                    this.notification.removeAll();
                    this.notification.success(this.translate.instant('TICKET_REFUND_SUCCESSFUL'));
                    this.refreshTickets.emit();
                    await this.modalCtrl.dismiss().catch(() => undefined);
                },
                (e) => {
                    this.notification.removeAll();
                    this.notification.error(this.translate.instant(e.error));
                }
            );
    }

    private handleLendTicket(): void {
        const dateTime = this.formatZdt(
            Temporal.PlainDateTime.from(this.validUntil.datetime).toZonedDateTime(this.appStartUp.getTimezone())
        );
        this.isLoading = true;
        this.deviceIdService
            .getDeviceCustomerId()
            .pipe(
                take(1),
                defaultIfEmpty(null),
                switchMap((deviceCustomerId) => {
                    const useDeviceToken = this.shouldUseDeviceToken(deviceCustomerId);
                    return this.accountTicketsService.lendTicket(
                        this.docNum,
                        this.form.get('email').value,
                        dateTime,
                        useDeviceToken
                    );
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(
                async () => {
                    this.isLoading = false;
                    this.notification.removeAll();
                    await this.modalCtrl.dismiss();
                    this.refreshTickets.emit();
                    await this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
                    this.notification.success(this.translate.instant('TICKET_LEND_SUCCESSFUL'), {
                        showAfterRouteChange: true
                    });
                },
                (e: TicketActionHttpErrorResponse) => this.handleTicketActionError(e.error)
            );
    }

    private handleReturnOrRecallLendTicket(): void {
        this.isLoading = true;
        this.deviceIdService
            .getDeviceCustomerId()
            .pipe(
                take(1),
                defaultIfEmpty(null),
                switchMap((deviceCustomerId) => {
                    const useDeviceToken = this.shouldUseDeviceToken(deviceCustomerId);
                    return this.action === 'RETURN_LEND_TICKET'
                        ? this.accountTicketsService.returnLendTicket(this.docNum, useDeviceToken)
                        : this.accountTicketsService.recallLendTicket(this.docNum, useDeviceToken);
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(
                async () => {
                    this.isLoading = false;
                    this.notification.removeAll();
                    this.refreshTickets.emit();
                    await this.modalCtrl.dismiss();
                    await this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
                    this.notification.success(this.translate.instant('TICKET_RETURN_SUCCESSFUL'), {
                        showAfterRouteChange: true
                    });
                },
                (e: TicketActionHttpErrorResponse) => this.handleTicketActionError(e.error)
            );
    }

    private async handleTicketActionError(error: TicketActionErrorResponse): Promise<void> {
        this.isLoading = false;
        this.notification.removeAll();
        await this.modalCtrl.dismiss().catch(() => undefined);
        this.notification.error(this.translate.instant(this.getErrorCode(error)));
    }

    private getErrorCode(error: TicketActionErrorResponse): string {
        switch (typeof error) {
            case 'string':
                return error || 'INTERNAL_ERROR';

            case 'object':
                return error?.code || error?.message || 'INTERNAL_ERROR';

            default:
                return 'INTERNAL_ERROR';
        }
    }

    private formatZdt(zdt: Temporal.ZonedDateTime) {
        const p = (n: number) => n.toString().padStart(2, '0');
        return `${p(zdt.day)}.${p(zdt.month)}.${zdt.year} ${p(zdt.hour)}:${p(zdt.minute)}`;
    }

    get anyAllowedTravelCards(): boolean {
        return !!this.travelCards && this.travelCards.length > 0;
    }

    onLentToChanged() {
        this.validUntilFormatted = moment.tz(this.validUntil.datetime, this.env.TIMEZONE).format('DD.MM.yyyy HH:mm');
    }

    private setDefaultSelectedLendTime(): void {
        const defaultDateTime = moment().add(4, 'h');

        this.selectedLendTime = {
            value: 4,
            date: defaultDateTime.format('YYYY-MM-DD HH:mm'),
            label: `4 ${this.translate.instant('HOURS')}`
        };
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
